/**
 * Upsell popup controller (+ free-shipping nudge).
 *
 * On ANY add-to-cart (standard `shopify:cart:lines-update` event) it:
 *   1. Identifies the added ("trigger") product from the cart.
 *   2. Fetches that product's upsell card(s) via the Section Rendering API
 *      (`/products/<handle>?section_id=upsell-popup`, which reads the
 *      `custom.custom_upsell_product` metafield — single product OR a list).
 *   3. Shows the modal BEFORE the cart drawer. Each upsell has its own "Add"
 *      button, so the shopper can add several; the popup stays open until they
 *      dismiss it, then the cart drawer opens.
 *
 * FREE SHIPPING NUDGE (threshold = data-fs-threshold on <upsell-popup>, cents):
 *   • When the cart is under the threshold, the popup shows a "You're only $X
 *     away from FREE shipping" line above the upsell cards.
 *   • When the trigger product has NO metafield upsell, we fall back to booster
 *     cards from `/?section_id=free-shipping-popup` (products that cover the
 *     remaining gap) with the free-shipping message as the heading — e.g. cart
 *     at $79 → "You're only $1.00 away from FREE shipping!" + the $9.99 scrub.
 *   • After an add that crosses the threshold, the message flips to
 *     "FREE shipping unlocked!".
 *   • UK (GBP, 2026-10-15): the threshold is converted at Shopify.currency.rate
 *     (see fsThresholdFor), and the booster section sends every variant as a
 *     [data-fs-candidate] that extractCards() narrows to the ones covering the gap.
 *
 * TRACKING (visible in Shopify admin on the order):
 *   • Every popup add carries a hidden line-item property `_Added via`
 *     ("Upsell popup" / "Free shipping popup") shown under the order line item.
 *   • Free-shipping upsell adds also set the cart attribute
 *     `lucy_freeshipping_upsell: yes` (naming matches the existing
 *     `lucy_upsell_clicked` convention), which lands in the order's custom
 *     attributes — the Shopify Flow workflow "Tag orders with free shipping
 *     upsell" tags those orders `lucy-freeshipping-upsell` on creation.
 *
 * HAT FREE-SHIPPING OFFER TEST: a B2G1 card with data-fs-offer comes from
 * snippets/upsell-card-hat-b2g1.liquid (AU shoppers with exactly 1 hat). On
 * show we set cart attribute `lucy_hat_fs_offer: shown`. On add we apply the
 * card's data-fs-offer-code and record the take BEFORE adding the hats, so the
 * cart drawer re-render already shows free shipping unlocked.
 *
 * SCRUB OFFER AUTO-CLOSE (2026-09-21): once the scrub free-shipping card is
 * added the popup's job is done, so it closes itself after a short beat (long
 * enough to see "✓ Added" + "FREE shipping unlocked!") and hands over to the
 * cart drawer — the shopper never has to hit ✕. Its "$X shipping without the
 * scrub" disclaimer is hidden at the same moment, since it no longer applies.
 *
 * We take over the cart drawer's auto-open (remove the `auto-open` attribute) so the
 * popup comes first, and only re-open the cart ourselves if auto-open was enabled.
 * Any failure falls back to opening the cart. Frequency: one popup per trigger
 * product per session (metafield mode), one per session (free-shipping fallback).
 */
import { CartLinesUpdateEvent } from '@shopify/events';

(function () {
  const root = document.querySelector('upsell-popup');
  if (!root) return;

  const cartDrawer = document.querySelector('cart-drawer-component');
  if (!cartDrawer) return; // drawer-cart only

  const dialog = root.querySelector('.upsell-popup__dialog');
  const contentEl = root.querySelector('[data-upsell-content]');
  const headingEl = root.querySelector('[data-upsell-heading]');
  const shippingEl = root.querySelector('[data-upsell-shipping]');
  const dismissEls = root.querySelectorAll('[data-upsell-dismiss]');
  const section = root.dataset.section || 'upsell-popup';
  const fsSection = root.dataset.fsSection || '';
  // UK (GBP): data-fs-threshold is the A$ figure, but FREESHIPPING100's A$100
  // minimum is converted at the market rate, so convert it the same way —
  // rounded UP to the whole pound. No rate → 0, which turns the nudge off
  // rather than showing a wrong number. Keep in sync with convertedThreshold()
  // in assets/free-shipping-nudge.js.
  function fsThresholdFor(shopCents) {
    const cur = window.Shopify && window.Shopify.currency;
    if (!cur || cur.active !== 'GBP') return shopCents;
    const rate = parseFloat(cur.rate);
    if (!shopCents || !rate) return 0;
    return Math.ceil((shopCents * rate) / 100) * 100;
  }
  const fsThreshold = fsThresholdFor(parseInt(root.dataset.fsThreshold, 10) || 0);
  if (!dialog || !contentEl) return;

  const themeDrawer = document.getElementById('cart-drawer');
  const autoOpenWasOn = cartDrawer.hasAttribute('auto-open');
  if (autoOpenWasOn) cartDrawer.removeAttribute('auto-open');

  const routes = (window.Theme && window.Theme.routes) || {};

  // Popup state for the free-shipping messaging
  let popupMode = 'upsell'; // 'upsell' | 'fs'
  let openedUnderThreshold = false;
  let overrideHeading = ''; // set per-open from a card's data-popup-heading (B2G1)
  let fsOffer = ''; // '' | 'shown' | 'taken' — hat free-shipping offer test state
  let fsOfferShownReq = null; // so a fast tap can't be overwritten by the late 'shown' write
  let scrubUnlocked = false; // scrub free-shipping card added in this popup
  const SCRUB_AUTOCLOSE_MS = 1200; // pause on the "unlocked" state before closing

  function openCart() {
    if (!autoOpenWasOn) return;
    try { if (themeDrawer && typeof themeDrawer.open === 'function') themeDrawer.open(); } catch (e) { /* no-op */ }
  }
  function closePopup() { try { if (dialog.open) dialog.close(); } catch (e) { /* no-op */ } }

  function cartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((c) => { if (c.dataset.sectionId) ids.push(c.dataset.sectionId); });
    return ids;
  }
  async function getCart() {
    const res = await fetch((routes.cart_url || '/cart') + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    return res.json();
  }
  function numericId(v) { return String(v == null ? '' : v).replace(/\D/g, ''); }
  function findLineByVariant(cart, variantId) {
    const vid = numericId(variantId);
    if (!vid) return null;
    return (cart.items || []).find((i) => numericId(i.id) === vid || numericId(i.variant_id) === vid) || null;
  }
  function productInCart(cart, productId) {
    const pid = String(productId);
    return (cart.items || []).some((i) => String(i.product_id) === pid);
  }
  // variantId → the added variant, so the section can default the B2G1 style
  // selects to it (read via product.selected_variant). cartState is only a
  // cache-buster: the B2G1 card's contents depend on how many hats are in the
  // cart, so the URL must change when the cart does.
  async function fetchUpsellHtml(handle, variantId, cartState) {
    let url = '/products/' + handle + '?section_id=' + encodeURIComponent(section);
    if (variantId) url += '&variant=' + encodeURIComponent(variantId);
    if (cartState) url += '&_cs=' + encodeURIComponent(cartState);
    const res = await fetch(url, { headers: { Accept: 'text/html' }, credentials: 'same-origin' });
    if (!res.ok) return '';
    return (await res.text()).trim();
  }
  async function fetchFsHtml() {
    if (!fsSection) return '';
    const res = await fetch('/?section_id=' + encodeURIComponent(fsSection), { headers: { Accept: 'text/html' }, credentials: 'same-origin' });
    if (!res.ok) return '';
    return (await res.text()).trim();
  }

  // ---- Tracking helpers ----------------------------------------------------
  // Cart attributes flow onto the order (admin "Additional details") and are
  // what Shopify Flow uses to tag free-shipping-upsell orders.
  async function setCartAttributes(attrs, discount) {
    try {
      const body = { attributes: attrs };
      if (discount != null) body.discount = discount;
      await fetch(routes.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
    } catch (e) { /* tracking must never break the flow */ }
  }
  // Current cart codes with `code` added or removed, comma-joined for /cart/update.js
  // (replacing the list outright would wipe a code the shopper entered).
  async function discountListWith(code, add) {
    let codes = [];
    try { codes = ((await getCart()).discount_codes || []).map((d) => d.code); } catch (e) { /* no-op */ }
    codes = codes.filter((c) => String(c).toUpperCase() !== String(code).toUpperCase());
    if (add) codes.push(code);
    return codes.join(',');
  }
  function publishEvent(name, payload) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, payload);
      }
    } catch (e) { /* no-op */ }
  }
  // --------------------------------------------------------------------------

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function fsRemaining(cart) {
    if (!fsThreshold) return 0;
    return fsThreshold - (cart.total_price || 0);
  }

  // Sync the heading + shipping line with the current cart state.
  function renderShippingState(cart) {
    if (!headingEl) return;
    if (fsOffer && shippingEl) {
      headingEl.textContent = overrideHeading || headingEl.textContent;
      shippingEl.hidden = false;
      const taken = fsOffer === 'taken';
      shippingEl.classList.toggle('is-unlocked', taken);
      shippingEl.style.setProperty('--fs-pct', taken ? '100%' : '33%');
      shippingEl.innerHTML = taken
        ? '🎉 You’ve unlocked <strong>FREE shipping</strong>!'
        : '🎁 Add 2 hats, <strong>pay for 1</strong> — and <strong>shipping’s FREE</strong>';
      return;
    }
    if (!fsThreshold) return;
    const remaining = fsRemaining(cart);
    const money = formatMoney(Math.max(remaining, 0), cart.currency);
    if (shippingEl) shippingEl.style.setProperty('--fs-pct', Math.min(100, Math.max(0, Math.round(100 - (remaining / fsThreshold) * 100))) + '%');

    if (popupMode === 'fs') {
      if (shippingEl) shippingEl.hidden = true;
      if (remaining <= 0 || scrubUnlocked) headingEl.textContent = '🎉 FREE shipping unlocked!';
      else headingEl.textContent = overrideHeading || 'You’re only ' + money + ' away from FREE shipping! 🚚';
      return;
    }

    headingEl.textContent = overrideHeading || headingEl.dataset.defaultHeading || headingEl.textContent;
    if (!shippingEl) return;
    if (remaining > 0) {
      shippingEl.hidden = false;
      shippingEl.classList.remove('is-unlocked');
      shippingEl.innerHTML = '🚚 You’re only <strong>' + money + '</strong> away from <strong>FREE shipping</strong>!';
    } else if (openedUnderThreshold) {
      shippingEl.hidden = false;
      shippingEl.classList.add('is-unlocked');
      shippingEl.innerHTML = '🎉 You’ve unlocked <strong>FREE shipping</strong>!';
    } else {
      shippingEl.hidden = true;
    }
  }

  // Add one upsell variant (mirrors blocks/bundle-builder.liquid). detail.source
  // flags it so our own listener below ignores it. `sourceLabel` becomes the
  // hidden `_Added via` line-item property on the order.
  async function addVariant(variantId, sourceLabel) {
    return addItems([{ id: Number(variantId), quantity: 1 }], sourceLabel);
  }
  async function addItems(rawItems, sourceLabel) {
    const items = rawItems.map((i) => ({ id: Number(i.id), quantity: i.quantity || 1, properties: { '_Added via': sourceLabel || 'Upsell popup' } }));
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
        detail: { source: 'upsell-popup' },
      })
    );
    try {
      const res = await fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: cartSectionIds().join(',') }),
      });
      const data = await res.json();
      const cart = await getCart();
      if (data && data.status) {
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: true, items: cart.items, source: 'upsell-popup' } });
        return null;
      }
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'upsell-popup', sections: data.sections, didError: false } });
      return cart;
    } catch (err) {
      deferred.reject(err);
      return null;
    }
  }

  function wireCard(card) {
    const btn = card.querySelector('[data-upsell-add-card]');
    if (!btn) return;
    const isB2g1 = card.hasAttribute('data-b2g1');
    const offerTaken = card.dataset.fsOffer || '';
    const isScrubOffer = card.hasAttribute('data-scrub-offer');
    btn.addEventListener('click', async () => {
      if (card.classList.contains('is-added')) return;
      const handle = card.dataset.upsellHandle || '';
      const isFs = popupMode === 'fs';
      const orig = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';

      // Free-shipping offer: apply the code + record the choice first, so the
      // drawer sections fetched by the add already render free shipping.
      if (offerTaken) await fsOfferShownReq;
      if (offerTaken) await setCartAttributes({ lucy_hat_fs_offer: offerTaken }, await discountListWith(card.dataset.fsOfferCode || '', true));

      let cart;
      if (isB2g1) {
        // One item per style select, same-variant picks merged into quantity.
        // The "Buy 2 Get 1 Free" automatic discount does the pricing.
        const counts = {};
        card.querySelectorAll('select[data-b2g1-variant]').forEach((s) => {
          counts[s.value] = (counts[s.value] || 0) + 1;
        });
        const items = Object.keys(counts).map((id) => ({ id, quantity: counts[id] }));
        cart = await addItems(items, offerTaken ? 'Upsell popup (B2G1 hat + free shipping)' : 'Upsell popup (B2G1 hat)');
      } else {
        cart = await addVariant(card.dataset.upsellVariantId, isScrubOffer ? 'Free shipping popup (scrub)' : (isFs ? 'Free shipping popup' : 'Upsell popup'));
      }

      if (cart) {
        if (offerTaken) fsOffer = 'taken';
        if (isScrubOffer) scrubUnlocked = true;
        card.classList.add('is-added');
        btn.textContent = '✓ Added';
        card.querySelectorAll('select[data-b2g1-variant]').forEach((s) => { s.disabled = true; });
        renderShippingState(cart);
        if (isScrubOffer) {
          setCartAttributes({ lucy_scrub_fs_offer: 'took_popup' });
          const disclaimer = contentEl.querySelector('[data-scrub-disclaimer]');
          if (disclaimer) disclaimer.hidden = true;
          setTimeout(() => { if (dialog.open) dismiss(); }, SCRUB_AUTOCLOSE_MS);
        }
        else if (isFs) setCartAttributes({ 'lucy_freeshipping_upsell': 'yes' });
        if (isB2g1) setCartAttributes({ 'lucy_b2g1_upsell': 'yes' });
        publishEvent('upsell_popup_added', {
          popup_type: isScrubOffer ? 'scrub_free_shipping' : offerTaken ? 'hat_b2g1_fs' : (isB2g1 ? 'hat_b2g1' : (isFs ? 'free_shipping' : 'product_upsell')),
          product_handle: handle,
          variant_id: isB2g1 ? '' : String(card.dataset.upsellVariantId),
          cart_total: cart.total_price,
        });
      } else {
        if (offerTaken) setCartAttributes({ lucy_hat_fs_offer: 'shown' }, await discountListWith(card.dataset.fsOfferCode || '', false)); // add failed: undo the code
        btn.disabled = false;
        btn.textContent = orig;
      }
    });
  }

  function extractCards(html, cart, triggerProductId) {
    contentEl.innerHTML = html;
    // UK booster candidates (sections/free-shipping-popup.liquid): keep the
    // first variant per product that covers the gap, drop the rest.
    const remaining = fsRemaining(cart);
    const keptHandles = {};
    Array.prototype.slice.call(contentEl.querySelectorAll('[data-fs-candidate]')).forEach((w) => {
      const h = w.dataset.fsHandle;
      if (keptHandles[h] || (parseInt(w.dataset.fsPrice, 10) || 0) < remaining) w.remove();
      else keptHandles[h] = true;
    });
    // Drop any card that's the trigger itself or already in the cart.
    Array.prototype.slice.call(contentEl.querySelectorAll('.upsell-card')).forEach((card) => {
      const pid = card.dataset.upsellProductId;
      if (pid && (triggerProductId != null && String(pid) === String(triggerProductId) || productInCart(cart, pid))) (card.closest('[data-fs-candidate]') || card).remove();
    });
    return Array.prototype.slice.call(contentEl.querySelectorAll('.upsell-card'));
  }

  async function handleAdd(event) {
    try {
      if (event.promise) {
        const result = await event.promise;
        if (result && result.detail && result.detail.didError) { openCart(); return; }
      }
    } catch (e) { openCart(); return; }

    let cart;
    try { cart = await getCart(); } catch (e) { openCart(); return; }
    if (!cart || !cart.item_count) return;

    const lines = event.lines || [];
    const firstVariant = lines[0] && (lines[0].merchandiseId || lines[0].id);
    let triggerLine = findLineByVariant(cart, firstVariant);
    if (!triggerLine) triggerLine = cart.items[cart.items.length - 1];
    if (!triggerLine) { openCart(); return; }

    const triggerProductId = triggerLine.product_id;
    const handle = triggerLine.handle;
    const remaining = fsRemaining(cart);

    let cards = [];
    popupMode = 'upsell';
    overrideHeading = '';
    fsOffer = '';
    scrubUnlocked = false;

    // 1) Product-specific upsell for the trigger product (once per product per
    //    session): B2G1 hat card or metafield card(s), decided server-side.
    const seenKey = 'upsell:' + triggerProductId;
    if (!sessionStorage.getItem(seenKey)) {
      let html = '';
      const cartState = (cart.item_count || 0) + '-' + (cart.total_price || 0);
      try { html = await fetchUpsellHtml(handle, numericId(triggerLine.variant_id), cartState); } catch (e) { html = ''; }
      if (html) {
        cards = extractCards(html, cart, triggerProductId);
        if (cards.length) sessionStorage.setItem(seenKey, '1');
      }
    }
    const headingCard = cards.find((c) => c.dataset.popupHeading);
    if (headingCard) overrideHeading = headingCard.dataset.popupHeading;
    if (cards.some((c) => c.dataset.fsOffer)) {
      fsOffer = 'shown';
      fsOfferShownReq = setCartAttributes({ lucy_hat_fs_offer: 'shown' });
    }

    // 2) Fallback: free-shipping booster popup (once per session) when the cart
    //    is under the threshold and there's no product upsell to show.
    if (!cards.length && remaining > 0 && fsSection && !sessionStorage.getItem('fs-popup:seen')) {
      let html = '';
      try { html = await fetchFsHtml(); } catch (e) { html = ''; }
      if (html) {
        cards = extractCards(html, cart, null);
        if (cards.length) {
          popupMode = 'fs';
          sessionStorage.setItem('fs-popup:seen', '1');
          const fsHeadingCard = cards.find((c) => c.dataset.popupHeading); // scrub offer card
          overrideHeading = fsHeadingCard ? fsHeadingCard.dataset.popupHeading : '';
        }
      }
    }

    if (!cards.length) { openCart(); return; }
    cards.forEach(wireCard);

    openedUnderThreshold = remaining > 0;
    renderShippingState(cart);

    publishEvent('upsell_popup_viewed', {
      popup_type: cards.some((c) => c.hasAttribute('data-scrub-offer')) ? 'scrub_free_shipping' : popupMode === 'fs' ? 'free_shipping' : (fsOffer ? 'hat_fs_offer' : 'product_upsell'),
      trigger_product: handle,
      products_shown: cards.map((c) => c.dataset.upsellHandle || c.dataset.upsellProductId),
      cart_total: cart.total_price,
      remaining_to_free_shipping: Math.max(remaining, 0),
    });

    const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
    if (sourceModal && sourceModal.open && sourceModal !== dialog) {
      sourceModal.addEventListener('close', () => { try { dialog.showModal(); } catch (e) { openCart(); } }, { once: true });
      try { sourceModal.close(); } catch (e) { /* no-op */ }
    } else {
      try { dialog.showModal(); } catch (e) { openCart(); }
    }
  }

  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (event.action !== 'add') return;
    const src = event.detail && event.detail.source;
    if (src === 'upsell-popup' || src === 'free-shipping-nudge') return; // ignore our own adds
    if (src === 'dad-kit-buy') { // Sauna Kit buyers: no hat upsell after a $100 kit, straight to the drawer
      Promise.resolve(event.promise).then(openCart, openCart);
      return;
    }
    handleAdd(event);
  });

  function dismiss() { closePopup(); openCart(); }
  dismissEls.forEach((el) => el.addEventListener('click', dismiss));
  dialog.addEventListener('cancel', (e) => { e.preventDefault(); dismiss(); });
  dialog.addEventListener('click', (e) => { if (e.target === dialog) dismiss(); });
})();
